import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import { Form } from '@components/forms/Form';
import { FormSection } from '@components/forms/FormSection';
import { FormFieldType, TFormField } from '@models/forms/formFields.type';
import { QuoteType, TQuoteApiResponse } from '@models/quotes';
import { useQuote } from '@services/quotes/useQuote';
import { Field } from '@pages/NewQuote/scope';

interface NewRateChangeAnalysisFormValues {
  rateVersionToCompare: string;
}

export interface NewRateChangeAnalysisContentProps {
  quote?: TQuoteApiResponse;
  onFormValidityChange: (isValid: boolean) => void;
  onRateVersionReady: (getRateVersion: () => number | undefined) => void;
  isDisabled?: boolean;
}

export const NewRateChangeAnalysisContent = ({
  quote,
  onFormValidityChange,
  onRateVersionReady,
  isDisabled,
}: NewRateChangeAnalysisContentProps) => {
  const isRenewal = quote?.quoteType === QuoteType.Renewal;
  const { quote: renewalSourceQuote } = useQuote(isRenewal ? (quote?.renewalSourceQuoteId ?? undefined) : undefined);

  const defaultRateVersion = renewalSourceQuote?.rateVersion;

  const methods = useForm<NewRateChangeAnalysisFormValues>({
    defaultValues: {
      rateVersionToCompare: defaultRateVersion?.toString() ?? '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (defaultRateVersion != null && !methods.getValues('rateVersionToCompare')) {
      methods.setValue('rateVersionToCompare', defaultRateVersion.toString());
    }
  }, [defaultRateVersion, methods]);

  const formFields: Record<string, TFormField> = useMemo(
    () => ({
      rateVersionToCompare: {
        gridWidth: { xs: 12 },
        name: 'rateVersionToCompare',
        label: 'Rate Version to Compare',
        fieldType: FormFieldType.TextField,
        required: true,
        fieldConfig: {
          textField: {
            inputType: 'number',
          },
        },
      },
    }),
    [],
  );

  const rateVersionValue = methods.watch('rateVersionToCompare');
  const isFormValid = rateVersionValue !== '' && !isNaN(parseFloat(rateVersionValue));

  useEffect(() => {
    onFormValidityChange(isFormValid);
  }, [isFormValid, onFormValidityChange]);

  useEffect(() => {
    onRateVersionReady(() => {
      const values = methods.getValues();
      const rateVersion = parseFloat(values.rateVersionToCompare);
      return !isNaN(rateVersion) ? rateVersion : undefined;
    });
  }, [methods, onRateVersionReady]);

  return (
    <Box sx={{ p: 3, minHeight: '400px' }}>
      <Box sx={{ mb: 3 }}>
        {isRenewal && (
          <Field value='Prior Term Rate Version:' label={renewalSourceQuote?.rateVersion?.toString() ?? '-'} />
        )}
        <Field value='Current Term Rate Version:' label={quote?.rateVersion?.toString() ?? '-'} />
      </Box>
      <Form methods={methods} formId='new-rate-change-analysis'>
        <FormSection fields={formFields} isDisabled={isDisabled} />
      </Form>
    </Box>
  );
};
